import { emailForm } from './downloadResults'
import xss from 'xss'

// A user's answer to a single question, captured as the swiper advances.
interface SlideEntry {
  question: string
  answer: string
  value: string
}

// Normalized result row rendered into the summary slides and the PDF. Shapes
// vary by question type, so most fields are optional.
interface ResultEntry {
  question: string
  resLabel: string
  resTitle: string
  res?: string
  desc?: string | string[]
  value?: string
  rollOverIRA?: string
  leaveAssetsInCurrentPlan?: string
}

// A single selectable answer within a question. `description`/`description2`
// hold either one paragraph or several; `description2` is the alternate copy
// shown when an age threshold is crossed (see resultsObjNum). The results-* keys
// carry the pros/cons copy surfaced on the summary tabs.
interface RolloverQuestionOption {
  type?: string
  name?: string
  value?: string
  title?: string
  answerLabel?: string
  description?: string | string[]
  description2?: string | string[]
  resultsIRARollOverPros?: string
  resultsLeaveAssetsCons?: string
}

// One question in the evaluator. `options` is a single option for text/number
// inputs and an array for radio inputs — call sites normalize via Array.isArray.
interface RolloverQuestion {
  name: string
  type: string
  title: string
  descriptionLabel: string
  errorMessage?: string
  options?: RolloverQuestionOption | RolloverQuestionOption[]
}

// Tooltip copy keyed by id. `questionMessages` may be absent for some datasets,
// so reads must stay optional-safe.
interface RolloverTooltips {
  yourOptionMessages: { id: string; message: string }[]
  questionMessages?: { id: string; message: string }[]
}

// Pros/cons copy for one results tab. `list` entries are rewritten in place to
// unescape markup, so the arrays are mutable by design.
interface RolloverResultTab {
  name: string
  title: string
  overview?: string[]
  pros: { overview: string; list: string[] }
  cons: { overview: string; list: string[] }
}

interface RolloverResults {
  title: string
  overview: string
  subheading: string
  summaryHeading: string
  tabs: RolloverResultTab[]
}

// The evaluator content injected at runtime via `window.rolloverData`. Produced
// outside the client; the component only reads it.
interface RolloverData {
  tooltips: RolloverTooltips
  questions: RolloverQuestion[]
  results: RolloverResults
}

declare global {
  interface Window {
    rolloverData?: RolloverData
  }
}

const data = window.rolloverData as RolloverData

// Toggles background scroll by setting overflow on <html> (mirrors useSetBodyScroll hook).
function setBodyScroll(lock: boolean): void {
  const html = document.querySelector('html')
  if (html) {
    html.style.overflow = lock ? 'hidden' : 'auto'
  }
}

function lockBodyScroll(): void {
  setBodyScroll(true)
}

function unlockBodyScroll(): void {
  setBodyScroll(false)
}

// modals
const attachListeners = (
  toolTip: HTMLElement,
  button: HTMLElement,
  span?: HTMLElement
): void => {
  let toggle = false
  button.addEventListener('click', e => {
    e.preventDefault()
    toggle = !toggle
    toolTip.style.visibility = toggle ? 'visible' : 'hidden'
    arrowPosition(toolTip, button)
  })
  const doc = document.querySelector('.rollover-component')
  doc?.addEventListener('click', e => {
    if (e.target !== button && e.target !== span) {
      toggle = false
      toolTip.style.visibility = toggle ? 'visible' : 'hidden'
    }
  })
}

function tooltipListener(): void {
  const activeSlide = findActive()
  if (!activeSlide) return
  const question = activeSlide.querySelector<HTMLElement>('.rollover-question')
  const $questionName = question?.dataset.name
  const $tipSelector = `.Tooltip[for="${$questionName}"]`
  const toolTip = document.querySelector<HTMLElement>($tipSelector)
  const $selectorVal = `[data-name='${$questionName}'] a`
  const toolTipBtn = activeSlide.querySelector<HTMLElement>($selectorVal)
  if (toolTipBtn && toolTip) {
    let infoEl = toolTipBtn.querySelector<HTMLElement>('.adel-material-symbols')
    if (!infoEl) {
      infoEl = document.createElement('span')
      infoEl.classList.add('adel-material-symbols', 'amp-info')
      infoEl.innerHTML = 'info'
      infoEl.title = 'info'
      infoEl.role = 'img'
      infoEl.ariaLabel = 'info'
      toolTipBtn.appendChild(infoEl)
    }
    attachListeners(toolTip, toolTipBtn, infoEl)
  }
}

function dfnToolTIp(container: Element, data: RolloverData): void {
  const toolTipDFNAll = container.querySelectorAll<HTMLElement>(
    'dfn.c-dfn-withTooltip'
  )
  toolTipDFNAll.forEach(toolTipDfn => {
    const newToolTip = document.createElement('div')
    const toolTipContent = document.createElement('div')
    const newToolTipClassList = ['Tooltip', 'Tooltip-dfn', 'u-marginTop8']
    newToolTip.classList.add(...newToolTipClassList)
    toolTipContent.classList.add('Tooltip-content')
    const parent = toolTipDfn.parentNode as HTMLElement
    newToolTip.style.width = parent.offsetWidth + 'px'
    const dfnToolTipsOpts = data.tooltips.yourOptionMessages
    const dfnToolTipsQs = data.tooltips.questionMessages
    const dataFor = toolTipDfn.dataset.for
    const toolTipOptions = dfnToolTipsOpts.find(tips => tips.id === dataFor)
    const toolTipQuestions = dfnToolTipsQs?.find(tips => tips.id === dataFor)
    toolTipOptions
      ? (toolTipContent.innerHTML = toolTipOptions.message)
      : (toolTipContent.innerHTML = toolTipQuestions?.message ?? '')
    newToolTip.appendChild(toolTipContent)
    toolTipDfn.appendChild(newToolTip)
    toolTipDfn.setAttribute('tabindex', '-1')
    attachListeners(newToolTip, toolTipDfn)
  })
}

function aboutRolloverTooltip(): void {
  const aboutBtn = document.querySelector('.about-rollover button')
  if (aboutBtn) {
    const aboutModal = document.querySelector<HTMLElement>(
      '.Modal.rollover-about'
    )
    const aboutClose = aboutModal?.querySelector('button')
    aboutBtn.addEventListener('click', () => {
      if (aboutModal) aboutModal.style.display = 'block'
      lockBodyScroll()
    })
    aboutClose?.addEventListener('click', () => {
      if (aboutModal) aboutModal.style.display = 'none'
      unlockBodyScroll()
    })
  }
}

function arrowPosition(currentTooltip: HTMLElement, button: HTMLElement): void {
  const buttonPos = button.offsetLeft
  const tipPos = currentTooltip.offsetLeft
  const styleElem = document.head.appendChild(document.createElement('style'))
  if (button.classList.contains('c-dfn-withTooltip')) {
    const leftPos = buttonPos - tipPos + 'px'
    styleElem.innerHTML = `#${button.id} .Tooltip:before, #${button.id} .Tooltip:after  {left: ${leftPos};}`
  } else {
    const leftPos = buttonPos + 'px'
    styleElem.innerHTML = `.Tooltip:before, .Tooltip:after  {left: ${leftPos};}`
  }
}

// slides

const slideUpdater = () => {
  let slideObj: SlideEntry[] = []
  return {
    clear: (): void => {
      slideObj = []
    },
    removeDuplicates: (res: SlideEntry): SlideEntry[] => {
      slideObj.forEach(a => {
        if (a.question === res.question) {
          slideObj.splice(slideObj.indexOf(a))
        }
      })
      return slideObj
    }
  }
}
const updateSlides = slideUpdater()

function updateSlideObj(
  set: string | undefined,
  x: string | undefined,
  y: string | undefined,
  v: string | undefined
): void {
  const questionEntry: SlideEntry = {
    question: x ?? '',
    answer: y ?? '',
    value: v ?? ''
  }
  const updatedSet = updateSlides.removeDuplicates(questionEntry)
  updatedSet.push(questionEntry)

  if (checkCompletion(updatedSet)) {
    resultsShow()
    resultsData(updatedSet)
    raiseFormEvent()
  }
}

function resultsShow(): void {
  const rollEval = document.querySelector<HTMLElement>('.rollover-evaluator')
  const rollEvalRes = document.querySelector<HTMLElement>(
    '.rollover-results-view'
  )
  const resultsSummary = document.querySelector('.rollover-results-summary')
  const bottomContent = document.querySelector('.bottom-content')
  if (rollEval) {
    rollEval.style.display =
      !rollEval.style.display || rollEval.style.display === 'block'
        ? 'none'
        : 'block'
  }
  if (rollEvalRes) {
    rollEvalRes.style.display =
      !rollEvalRes.style.display || rollEvalRes.style.display === 'none'
        ? 'block'
        : 'none'
  }
  resultsSummary?.classList.remove('u-hidden')
  bottomContent?.classList.remove('u-hidden')
}

function raiseFormEvent(): void {
  const resultsSummary = document.querySelector('.rollover-results-summary')
  const e = new CustomEvent('resultsShow', {
    detail: { html: callEmailHtml() }
  })
  resultsSummary?.dispatchEvent(e)
}

function callEmailHtml(): Promise<string> | undefined {
  /* return form element from the results */
  const resultsDOM = document.querySelector(
    '#rollover-results-download-content-pdf'
  )
  if (resultsDOM) {
    const resultsHTML = resultsDOM.innerHTML || ''
    const formData = emailForm(resultsHTML)
    return formData
  }
  return undefined
}

function restartSlides(): void {
  const rollEval = document.querySelector<HTMLElement>('.rollover-evaluator')
  const rollEvalRes = document.querySelector<HTMLElement>(
    '.rollover-results-view'
  )
  const resultsSummary = document.querySelector('.rollover-results-summary')
  const bottomContent = document.querySelector('.bottom-content')
  if (rollEvalRes) rollEvalRes.style.display = 'none'
  if (rollEval) rollEval.style.display = 'block'
  resultsSummary?.classList.add('u-hidden')
  bottomContent?.classList.add('u-hidden')
  updateSlides.clear()
  document
    .querySelectorAll(".swiper-slide[data-target='card']")
    .forEach((slide, index) => {
      if (index !== 0) {
        slide.setAttribute('hidden', '')
      }
    })
  findSlides()
}

function findActive(): HTMLElement | null {
  return document.querySelector<HTMLElement>(
    ".swiper-slide.active[data-target='card']"
  )
}

function findSlides(): void {
  const activeSlide = findActive()
  if (activeSlide) {
    const slideQuestion = activeSlide.querySelector('.rollover-question')
    const slideQ = slideQuestion?.classList
    if (!slideQ) return

    if (slideQ.contains('Radio-Buttons')) {
      handleRadio()
    } else if (slideQ.contains('Date-Field')) {
      handleDate()
    } else if (slideQ.contains('Integer-Field')) {
      handleInteger()
    } else {
      enableSwipe()
    }
  }
}

function handleDate(): void {
  const activeSlide = findActive()
  if (!activeSlide) return
  const data = window.rolloverData as RolloverData
  const dobData = data.questions.filter(d => d.name === 'dateOfBirth')
  slideInputData(activeSlide, dobData[0])
  const dateRegex =
    /^((0?[1-9]|1[012])[/](0?[1-9]|[12][0-9]|3[01])[/](19|20)?[0-9]{4})$/
  const dateInput = activeSlide.querySelector<HTMLInputElement>('input')
  if (!dateInput) return
  let currentChar = 0
  if (dateInput.value) {
    enableSwipe()
  }

  dateInput.addEventListener('keyup', e => {
    const updatedValue = (e.target as HTMLInputElement).value
    const dateTest = dateRegex.test(dateInput.value)
    if (updatedValue.length > currentChar) {
      dateInput.value = addSlash(updatedValue)
        ? updatedValue + '/'
        : updatedValue
    }
    currentChar = updatedValue.length
    if (dateTest) {
      enableSwipe()
      toggleError(activeSlide)
    } else {
      disableSwipe()
    }
  })

  dateInput.addEventListener('focusout', () => {
    const dateTest = dateRegex.test(dateInput.value)
    if (dateTest) {
      toggleError(activeSlide)
    } else {
      toggleError(activeSlide, false)
      disableSwipe()
    }
  })
}

function addSlash(value = ''): boolean {
  return Boolean(value && (value.length === 2 || value.length === 5))
}

function slideInputData(
  activeSlide: HTMLElement,
  dataObj: RolloverQuestion
): void {
  const error = activeSlide.querySelector('.error')
  const hint = activeSlide.querySelector('.input-text')
  if (error) error.innerHTML = dataObj.errorMessage ?? ''
  const options = Array.isArray(dataObj.options)
    ? dataObj.options[0]
    : dataObj.options
  if (hint) hint.innerHTML = options?.title ?? ''
}

function handleRadio(): void {
  const activeSlide = findActive()
  if (!activeSlide) return
  const radioInput = activeSlide.querySelectorAll('input')
  radioInput.forEach(input => {
    input.addEventListener('click', () => {
      input.setAttribute('selected', '')
      enableSwipe()
    })
  })
  if (activeSlide.querySelector('input[selected]')) {
    enableSwipe()
  }
}

function handleInteger(): void {
  const numberRe = /^[0-9]+$/
  const activeSlide = findActive()
  if (!activeSlide) return
  const data = window.rolloverData as RolloverData
  const integerData = data.questions.filter(d => d.type === 'number')
  slideInputData(activeSlide, integerData[0])
  const intInput = activeSlide.querySelector<HTMLInputElement>('input')
  if (!intInput) return
  if (intInput.value) {
    enableSwipe()
  }
  const validateAgeInput = (
    _e: Event,
    enabledCallback: () => void = () => {},
    disabledCallback: () => void = () => {}
  ): void => {
    if (
      intInput.value &&
      intInput.value.match(numberRe) &&
      intInput.value.length < 4
    ) {
      enableSwipe()
      enabledCallback()
    } else {
      disableSwipe()
      disabledCallback()
    }
  }
  intInput.addEventListener('keyup', validateAgeInput)
  intInput.addEventListener('focusout', e =>
    validateAgeInput(
      e,
      () => toggleError(activeSlide),
      () => toggleError(activeSlide, false)
    )
  )
}

function toggleError(activeSlide: HTMLElement, hidden = true): void {
  const error = activeSlide.querySelector<HTMLElement>('.error')
  if (error) error.hidden = hidden
}

function enableSwipe(): void {
  const swiperContainer = document.querySelector(
    '.swiper-container.rollover-swiper'
  )
  const swiperNav = swiperContainer?.querySelector('.swiper-button-next')
  if (swiperNav?.classList.contains('swiper-button-disabled')) {
    swiperNav.classList.remove('swiper-button-disabled')
    swiperNav.removeAttribute('disabled')
  }
}
function disableSwipe(): void {
  const swiperContainer = document.querySelector(
    '.swiper-container.rollover-swiper'
  )
  const swiperNav = swiperContainer?.querySelector('.swiper-button-next')
  swiperNav?.classList.add('swiper-button-disabled')
  swiperNav?.setAttribute('disabled', 'true')
}

function getRes(slide: HTMLElement): void {
  const question = slide.querySelector<HTMLElement>('.rollover-question')
  const qstn = question?.dataset.name
  const inputTypes = slide.querySelectorAll('input')
  let selectedValue: string | undefined
  let selectedName: string | undefined
  let userRes: string | undefined

  inputTypes.forEach(input => {
    if (input.type === 'radio') {
      for (const radio of inputTypes) {
        if (radio.checked) {
          selectedValue = radio.value
          selectedName = radio.name
          userRes = slide.querySelector('input')?.innerHTML
          break
        }
      }
    } else if (input.type === 'text' || input.type === 'number') {
      const firstInput = slide.querySelector('input')
      selectedName = firstInput?.name
      selectedValue = firstInput?.value
      userRes = firstInput?.innerHTML
    }
  })
  updateSlideObj(selectedName, qstn, userRes, selectedValue)
}

// results

const processResults = () => {
  let results: ResultEntry[] = []
  return (
    question: RolloverQuestion,
    answer?: RolloverQuestionOption,
    value?: string
  ): void => {
    if (question.type === 'number') {
      results.push(resultsObjNum(question, answer, value))
    } else if (question.type === 'radio') {
      results.push(resultsObjRadio(question, answer))
    } else if (question.type === 'text') {
      results.push(resultsObjText(question, answer, value))
    }
    if (checkCompletion(results)) {
      downloadResults(results)
      newSlides(results)
      createResultsView(data, results)
      results = []
    }
  }
}
const resultsObj = processResults()

function resultsObjNum(
  question: RolloverQuestion,
  answer: Partial<RolloverQuestionOption> = {
    title: '',
    description: ['', '']
  },
  value?: string
): ResultEntry {
  return {
    question: question.title,
    resLabel: question.descriptionLabel,
    resTitle: value ?? '',
    desc: parseInt(value ?? '') >= 60 ? answer.description2 : answer.description
  }
}

function resultsObjRadio(
  question: RolloverQuestion,
  answer: Partial<RolloverQuestionOption> = {
    answerLabel: '',
    title: '',
    value: '',
    resultsIRARollOverPros: '',
    resultsLeaveAssetsCons: '',
    description: ['']
  }
): ResultEntry {
  const description = answer.description
  return {
    question: question.title,
    resLabel: question.descriptionLabel,
    res: !answer.answerLabel ? answer.title : answer.answerLabel,
    resTitle: answer.title ?? '',
    desc: Array.isArray(description) ? description[0] : description,
    value: answer.value,
    rollOverIRA: answer.resultsIRARollOverPros,
    leaveAssetsInCurrentPlan: answer.resultsLeaveAssetsCons
  }
}

function resultsObjText(
  question: RolloverQuestion,
  answer: Partial<RolloverQuestionOption> = { title: '', description: [''] },
  value?: string
): ResultEntry {
  const description = answer.description
  return {
    question: question.title,
    resLabel: question.descriptionLabel,
    res: value,
    resTitle: answer.title ?? '',
    desc: Array.isArray(description) ? description[0] : description
  }
}

export function resultsData(arr: SlideEntry[]): void {
  arr.forEach(slide => {
    const dataQuestions = data.questions
    const filteredRes = dataQuestions.filter(
      question => question.name === slide.question
    )
    filteredRes.forEach(res => {
      if (Array.isArray(res.options) && res.options.length >= 1) {
        const filtersOptions = res.options.filter(
          opt => opt.value === slide.value
        )
        resultsObj(filteredRes[0], filtersOptions[0])
      } else {
        const options = Array.isArray(res.options)
          ? res.options[0]
          : res.options
        resultsObj(filteredRes[0], options, slide.value)
      }
    })
  })
}

export function createResults(res: SlideEntry[]): void {
  res.forEach(slide => {
    const newCard = document.createElement('div')
    const newq = document.createElement('h3')
    const newRes = document.createElement('p')
    const newDesc = document.createElement('p')
    newq.appendChild(document.createTextNode(slide.question))
    newRes.appendChild(document.createTextNode(slide.answer))
    const resultsDiv = document.querySelector('.results')

    newCard.classList.add('swiper-slide')
    newCard.appendChild(newq)
    newCard.appendChild(newRes)
    newCard.appendChild(newDesc)
    resultsDiv?.appendChild(newCard)
  })
}

export function downloadResults(results: ResultEntry[]): void {
  const resultsViewHTMLDownload = document.querySelector(
    '.rollover-results-download-content .swiper-wrapper'
  )
  if (!resultsViewHTMLDownload) return
  const resultsLengthDownload = results.length
  resultsViewHTMLDownload.innerHTML = ''
  results.forEach((result, index) => {
    const pageDownload = index + 1
    const newResultDownload = document.createElement('div')
    newResultDownload.setAttribute('data-target', 'card')
    newResultDownload.innerHTML = `
    <div class="u-paddingVert8">
      <div class="wrapper">
        <div class="pagination">
            <h5 class="results-pagination u-textSizeMsrp1">Question ${pageDownload}/${resultsLengthDownload}</h5>
        </div>
        <div class="rollover-question-download u-paddingBottom16">
          <h3></h3>
            <p class="your-response u-paddingTop8"></p>
        </div>
      </div>
      <div class="Content rollover-response response-description">
        <p class="u-paddingTop8"></p>
      </div>
    </div>`

    const resQuestionDownload = newResultDownload.querySelector<HTMLElement>(
      '.rollover-question-download h3'
    )
    const resUserResponseDownload =
      newResultDownload.querySelector<HTMLElement>(
        '.rollover-question-download p'
      )
    const resDescDownload = newResultDownload.querySelector<HTMLElement>(
      '.response-description p'
    )
    if (resQuestionDownload) resQuestionDownload.innerHTML = result.question

    if (result.desc == null) {
      if (resDescDownload)
        resDescDownload.innerText = result.resLabel + ' ' + result.res
    } else {
      if (resDescDownload) {
        Array.isArray(result.desc)
          ? (resDescDownload.innerHTML = `${result.desc[0]}</br>${result.desc[1]}`)
          : (resDescDownload.innerHTML = result.desc)
      }
      if (resUserResponseDownload) {
        !result.resLabel.includes(':')
          ? (resUserResponseDownload.innerText =
              result.resLabel + ' ' + result.res)
          : (resUserResponseDownload.innerText =
              result.resLabel + ' ' + result.resTitle)
      }
    }
    resultsViewHTMLDownload.appendChild(newResultDownload)
  })
}

export function newSlides(results: ResultEntry[]): void {
  const resultsViewHTML = document.querySelector(
    '.rollover-results-view .swiper-wrapper'
  )
  if (!resultsViewHTML) return
  const resultsLength = results.length

  results.forEach((result, index) => {
    const page = index + 1
    const newSlide = document.createElement('div')
    newSlide.setAttribute('data-target', 'card')
    newSlide.className = 'swiper-slide u-posRelative u-flexShrink0 u-sizeFull'
    newSlide.innerHTML = `
        <div class="Content Card slide-content card-height u-bgColorWhite u-flex u-md-flexCol u-sm-flexCol u-md-sizeFull u-padding0 u-border">
          <div class="u-size6of12 u-md-sizeFull u-sm-sizeFull u-md-paddingVert24 u-sm-paddingVert24 arrow-right u-posRelative u-flex u-flexCol u-flexJustifyCenter">
            <div class="u-posRelative slide-height question-slide-summary u-flex u-md-flex u-sm-flex u-flexCol u-flexJustifyCenter">
              <div class="Content pagination u-flex u-paddingHoriz40">
                <p class="u-paddingHoriz8 u-paddingBottom40">Question</p>
                <p class="results-pagination">${page}/${resultsLength}</p>
              </div>
              <div class="Content rollover-question u-paddingHoriz48 u-marginCenter">
                <h3></h3>
                <div class="u-marginCenter u-textCenter">
                  <p class="your-response questionFont u-textOpen u-marginCenter u-paddingVert24 u-textBold u-textSizeMsrp1 u-sm-textSizeMsrm1 u-inlineBlock u-textLeft"></p>
                </div>
              </div>
            </div>
            </div>
            <div class="slide-height u-flex u-flexCol u-flexJustifyCenter u-paddingRight48 u-sm-paddingLeft0 u-paddingLeft24 u-size6of12 u-bgColorDarkMint u-md-sizeFull u-sm-sizeFull">
              <div class="Content Content--reversed rollover-response u-flex u-flexCol u-flexAlignItemsCenter u-marginCenter response-description">
                <p class="u-textColorWhite u-textSizeMsrp1 u-sm-textSizeMsrm1 u-sizeFull u-sm-textDefault"></p>
              </div>
            </div>
          </div>
        </div>`

    const resUserResponse = newSlide.querySelector<HTMLElement>(
      '.rollover-question p'
    )
    const resDesc = newSlide.querySelector<HTMLElement>(
      '.response-description p'
    )

    if (result.desc == null) {
      if (resDesc) resDesc.innerText = result.resLabel + ' ' + result.res
    } else {
      if (resDesc) {
        Array.isArray(result.desc)
          ? (resDesc.innerHTML = `${result.desc[0]}</br>${result.desc[1]}`)
          : (resDesc.innerHTML = result.desc)
      }
      if (resUserResponse) {
        !result.resLabel.includes(':')
          ? (resUserResponse.innerText = result.resLabel + ' ' + result.res)
          : (resUserResponse.innerText =
              result.resLabel + ' ' + result.resTitle)
      }
    }

    const sanitizedDiv = document.createElement('div')
    Array.from(newSlide.attributes).forEach(attr => {
      sanitizedDiv.setAttribute(attr.name, attr.value)
    })
    sanitizedDiv.innerHTML = xss(newSlide.innerHTML, {
      whiteList: {
        div: ['class', 'data-target', 'style'],
        p: ['class'],
        h3: ['class'],
        dfn: ['class', 'data-icon', 'id', 'data-for', 'tabindex'],
        em: [],
        a: ['href', 'class', 'data-icon']
      }
    })

    const resQuestion = sanitizedDiv.querySelector<HTMLElement>(
      '.rollover-question h3'
    )
    if (resQuestion) {
      resQuestion.innerHTML = result.question
      dfnToolTIp(resQuestion, data)
    }

    resultsViewHTML.appendChild(sanitizedDiv)
  })
}

export function createResultsView(
  data: RolloverData,
  resultsObj: ResultEntry[]
): void {
  const resultsView = document.querySelector<HTMLElement>(
    '.amp-ira-rollover-results'
  )
  const resultTabsData = data.results.tabs
  const summaryHeading = document.querySelector('.results-summary-heading')
  if (summaryHeading) summaryHeading.innerHTML = data.results.title
  const summaryOverview = document.querySelector('.results-summary p')
  if (summaryOverview) summaryOverview.innerHTML = data.results.overview
  const optionsTitle = document.querySelector('.options-title')
  if (optionsTitle) optionsTitle.innerHTML = data.results.subheading
  const summaryH3 = document.querySelector('.rollover-results-summary h3')
  if (summaryH3) summaryH3.innerHTML = data.results.summaryHeading

  if (!resultsView) return
  createResultTabs(resultTabsData, resultsView, resultsObj)
  resultsTabsSelection(resultTabsData, resultsView, resultsObj)
}

const createResultTabs = (
  resultTabsData: RolloverResultTab[],
  resultsView: HTMLElement,
  resultsObj: ResultEntry[]
): void => {
  const listTabsClassList =
    'listTabs List-item marginCenter u-marginHoriz32 u-textSizeMsr'
  const ulListTabs = resultsView.querySelector('div.tab-titles')
  const dropTabs = resultsView.querySelector('select.Dropdown-menu')

  resultTabsData.forEach(result => {
    const newTab = document.createElement('p')
    const newOption = document.createElement('option')
    newTab.setAttribute('role', 'tab')
    newTab.setAttribute('data-value', result.name)
    newOption.setAttribute('data-value', result.name)
    newOption.setAttribute('value', result.name)
    newTab.className = listTabsClassList
    newOption.classList.add('optionTabs')
    newOption.innerHTML = result.title
    newTab.innerHTML = result.title
    ulListTabs?.appendChild(newTab)
    dropTabs?.appendChild(newOption)
  })
  showThirdTab(resultsObj)
}

const showThirdTab = (responses: ResultEntry[]): void => {
  const thirdTab = document.querySelectorAll<HTMLElement>(
    '[data-value="rollOverToNewEmployer"]'
  )
  const tabOptions = responses.filter(
    userRes =>
      userRes.value === 'changingJobs' || userRes.value === 'previousEmployer'
  )
  if (tabOptions.length <= 0) {
    thirdTab.forEach(third => {
      third.style.display = 'none'
    })
  }
}

const createResultProsCONS = (
  resultTabsData: RolloverResultTab[],
  resultsView: HTMLElement,
  activeTab: HTMLElement,
  resultsObj: ResultEntry[]
): void => {
  const ulPros = resultsView.querySelector('ul.pros')
  const ulCons = resultsView.querySelector('ul.cons')
  const dataValue = activeTab.dataset.value
  const activeTabData = resultTabsData.filter(tabs => tabs.name === dataValue)
  const resOverView = document.querySelector('.results-overview')
  const activeTabEntry = activeTabData[0]
  if (!activeTabEntry) return

  if (activeTabEntry.overview) {
    activeTabEntry.overview.forEach(overView => {
      const newOverview = document.createElement('p')
      newOverview.innerHTML = overView
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
      resOverView?.appendChild(newOverview)
      dfnToolTIp(resOverView as Element, data)
    })
  }

  const proList = activeTabEntry.pros.list
  proList.forEach(
    (pro, index) =>
      (proList[index] = proList[index]
        .replace('<ul>', '')
        .replace('</ul>', '')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>'))
  )
  const proOverview = activeTabEntry.pros.overview
  if (ulPros) createList(proList, ulPros, proOverview)
  const conList = activeTabEntry.cons.list
  conList.forEach(
    (con, index) =>
      (conList[index] = conList[index]
        .replace('<ul>', '')
        .replace('</ul>', '')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>'))
  )
  const conOverview = activeTabEntry.cons.overview
  if (ulCons) createList(conList, ulCons, conOverview)

  resultsObj.forEach(obj => {
    if (obj.rollOverIRA || obj.leaveAssetsInCurrentPlan) {
      if (dataValue === 'rollOverIRA' && obj.rollOverIRA !== '') {
        if (ulPros && obj.rollOverIRA) createList(obj.rollOverIRA, ulPros)
      } else if (
        dataValue === 'leaveAssetsInCurrentPlan' &&
        obj.leaveAssetsInCurrentPlan !== ''
      ) {
        if (ulCons && obj.leaveAssetsInCurrentPlan)
          createList(obj.leaveAssetsInCurrentPlan, ulCons)
      }
    }
  })
}

const createResultProsCONSDownload = (
  resultTabsData: RolloverResultTab[],
  resultsObj: ResultEntry[]
): void => {
  const downloadTitles = [
    'rollOverIRA',
    'leaveAssetsInCurrentPlan',
    'rollOverToNewEmployer'
  ]
  const resultsSections = document.querySelector('.result-title-section')
  if (!resultsSections) return
  let activeTitle = ''
  let activeTabEntryDownload: RolloverResultTab | undefined
  let sectionCount = 3

  resultsSections.innerHTML = ''

  const tabOptions = resultsObj.filter(
    userRes =>
      userRes.value === 'changingJobs' || userRes.value === 'previousEmployer'
  )
  if (tabOptions.length <= 0) {
    sectionCount = 2
  }

  for (let i = 0; i < sectionCount; i++) {
    activeTitle = downloadTitles[i]
    const activeTabDataDownload = resultTabsData.filter(
      tabs => tabs.name === activeTitle
    )
    activeTabEntryDownload = activeTabDataDownload[0]

    const ulWrapperElement = document.createElement('div')
    ulWrapperElement.className = 'pdf-section'
    resultsSections.appendChild(ulWrapperElement)
    if (activeTabEntryDownload?.title) {
      const newTitle = document.createElement('h2')
      newTitle.className = 'u-paddingVert16'
      newTitle.innerHTML = activeTabEntryDownload.title
      ulWrapperElement.appendChild(newTitle)

      activeTabEntryDownload.overview?.forEach(overView => {
        const newOverview = document.createElement('p')
        newOverview.innerHTML = overView
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
        ulWrapperElement.appendChild(newOverview)
      })

      const proTitle = document.createElement('h5')
      proTitle.className = 'u-textSizeMsrp1 u-paddingTop8'
      proTitle.innerHTML = 'Pros'
      ulWrapperElement.appendChild(proTitle)

      if (activeTabEntryDownload.pros.overview) {
        const newProsOverview = document.createElement('p')
        newProsOverview.className = 'pros-overview'
        newProsOverview.innerHTML = activeTabEntryDownload.pros.overview
        ulWrapperElement.appendChild(newProsOverview)
      }

      const newProsUL = document.createElement('ul')
      newProsUL.className = 'pros'
      ulWrapperElement.appendChild(newProsUL)

      const proListDownload = activeTabEntryDownload.pros.list
      proListDownload.forEach(
        (pro, index) =>
          (proListDownload[index] = proListDownload[index]
            .replace('<ul>', '')
            .replace('</ul>', '')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>'))
      )
      downloadList(proListDownload, newProsUL)

      const conTitle = document.createElement('h5')
      conTitle.className = 'u-textSizeMsrp1 u-paddingTop8'
      conTitle.innerHTML = 'Cons'

      const conListDownload = activeTabEntryDownload.cons.list
      conListDownload.forEach(
        (con, index) =>
          (conListDownload[index] = conListDownload[index]
            .replace('<ul>', '')
            .replace('</ul>', '')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>'))
      )
      ulWrapperElement.appendChild(conTitle)

      const newCosUL = document.createElement('ul')
      newCosUL.className = 'cons'
      ulWrapperElement.appendChild(newCosUL)
      downloadList(conListDownload, newCosUL)
    }
  }
}

function downloadList(listItems: string[] | string, ul: HTMLElement): void {
  if (Array.isArray(listItems)) {
    listItems.forEach(item => {
      const newItem = document.createElement('ul')
      newItem.className = 'u-marginLeft0'
      newItem.innerHTML = item
      ul.appendChild(newItem)
    })
  } else {
    const newItem = document.createElement('li')
    newItem.className = 'u-paddingLeft8 u-paddingBottom4'
    newItem.innerHTML = listItems
    ul.appendChild(newItem)
  }
}

function createList(
  listItems: string[] | string,
  ul: Element,
  listDesc?: string
): void {
  if (listDesc) {
    if (ul.previousElementSibling) {
      ul.previousElementSibling.innerHTML = listDesc
    }
  }
  // Preserves the original behavior: assigning an array to innerHTML coerces it
  // to a comma-joined string.
  ul.innerHTML = Array.isArray(listItems) ? listItems.join(',') : listItems
  dfnToolTIp(ul, data)
}

export function resultsTabsSelection(
  resultTabsData: RolloverResultTab[],
  resultsView: HTMLElement,
  resultsObj: ResultEntry[]
): void {
  const resTabs = document.querySelectorAll<HTMLElement>('.listTabs')
  const activeTabInit = document.querySelector<HTMLElement>(
    '.tab-titles .listTabs:first-of-type'
  )
  const activeDropdownInit = document.querySelector(
    '.Dropdown-menu .optionTabs:first-of-type'
  )
  const selectedClassList =
    'listTabs List-item marginCenter u-marginHoriz32 u-textSizeMsr is-selected u-textBold'
  const tabsClassList =
    'listTabs List-item marginCenter u-marginHoriz32 u-textSizeMsr'
  if (activeTabInit) activeTabInit.className = selectedClassList
  activeDropdownInit?.classList.add('is-selected')
  activeDropdownInit?.setAttribute('selected', 'selected')
  if (activeTabInit)
    createResultProsCONS(resultTabsData, resultsView, activeTabInit, resultsObj)
  createResultProsCONSDownload(resultTabsData, resultsObj)

  resTabs.forEach(tab => {
    tab.addEventListener('click', e => {
      e.preventDefault()
      clearTabResults(resultsView)
      const activeTab = document.querySelector('.listTabs.is-selected')
      if (activeTab) activeTab.className = tabsClassList
      const target = e.target as HTMLElement
      target.className = selectedClassList
      createResultProsCONS(resultTabsData, resultsView, target, resultsObj)
      createResultProsCONSDownload(resultTabsData, resultsObj)
    })
  })
  const menu = document.querySelector('.Dropdown-menu')
  menu?.addEventListener('change', e => {
    e.preventDefault()
    clearTabResults(resultsView)
    const activeTab = menu.querySelector('.optionTabs.is-selected')
    activeTab?.classList.remove('is-selected')
    const targetValue = `[data-value=${(e.target as HTMLSelectElement).value}]`
    const selectedTab = resultsView.querySelector<HTMLElement>(targetValue)
    selectedTab?.classList.add('is-selected')
    if (selectedTab)
      createResultProsCONS(resultTabsData, resultsView, selectedTab, resultsObj)
    createResultProsCONSDownload(resultTabsData, resultsObj)
  })
}

export function clearTabResults(resultsView: HTMLElement): void {
  const ulPros = resultsView.querySelector('ul.pros')
  const ulCons = resultsView.querySelector('ul.cons')
  const overview = resultsView.querySelector('.pros-overview')
  const resOverView = document.querySelector('.results-overview')
  if (ulPros) ulPros.innerHTML = ''
  if (ulCons) ulCons.innerHTML = ''
  if (overview) overview.innerHTML = ''
  if (resOverView) resOverView.innerHTML = ''
}

export function clearResultsPage(): void {
  const resultsView = document.querySelector<HTMLElement>(
    '.amp-ira-rollover-results'
  )
  if (resultsView) clearTabResults(resultsView)
  const tabTitles = document.querySelector('div.tab-titles')
  if (tabTitles) tabTitles.innerHTML = ''
  const dropdownMenu = document.querySelector('.Dropdown-menu')
  if (dropdownMenu) dropdownMenu.innerHTML = ''
  document
    .querySelectorAll('.swiper-container.results .swiper-wrapper .swiper-slide')
    .forEach(slide => slide.parentNode?.removeChild(slide))
}

export function checkCompletion(arr: unknown[]): boolean {
  const totalSlides = document.querySelectorAll(
    '.rollover-evaluator .swiper-slide'
  )
  return totalSlides.length === arr.length
}

export function restartResultsSlider(slider: {
  slideTo: (index: number) => void
}): void {
  slider.slideTo(0)
  const prevButton = document.querySelector(
    '.rollover-results-summary .swiper-button-prev'
  )
  prevButton?.classList.add('swiper-button-disabled')
  prevButton?.parentNode?.replaceChild(prevButton.cloneNode(true), prevButton)

  const nextButton = document.querySelector(
    '.rollover-results-summary .swiper-button-next'
  )
  nextButton?.classList.remove('swiper-button-disabled')
  nextButton?.parentNode?.replaceChild(nextButton.cloneNode(true), nextButton)
}

export type { RolloverData }

export {
  restartSlides,
  findActive,
  findSlides,
  enableSwipe,
  disableSwipe,
  getRes,
  addSlash,
  handleDate,
  handleInteger,
  tooltipListener,
  dfnToolTIp,
  aboutRolloverTooltip,
  lockBodyScroll,
  unlockBodyScroll
}
